import { useState, useEffect, useRef, useCallback } from 'react'
import api from '../services/api'

/**
 * useChat — polling-based chat hook.
 * Polls every 5s when active, 30s when backgrounded.
 *
 * @param {'project'|'milestone'|'dm'} scope
 * @param {number} id  — project_id, milestone_id, or (for dm) the other user's id
 * @param {boolean} open — whether the chat panel is visible
 */
export function useChat(scope, id, open) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const lastIdRef = useRef(0)
  const intervalRef = useRef(null)

  const endpoint =
    scope === 'milestone' ? `/chat/milestone/${id}` :
    scope === 'dm' ? `/chat/dm/${id}` :
    `/chat/project/${id}`

  // Guards against out-of-order async responses: if the user switches conversation (id/scope)
  // while a fetch for the PREVIOUS id is still in flight, that stale response must not be
  // allowed to land and stomp the new conversation's messages with empty/wrong data — this
  // was the "chat sometimes disappears when you click" bug.
  const requestIdRef = useRef(0)

  const fetchMessages = useCallback(async (full = false) => {
    if (!id) return
    const myRequestId = requestIdRef.current
    try {
      const after = full ? 0 : lastIdRef.current
      const r = await api.get(`${endpoint}?after_id=${after}`)
      if (myRequestId !== requestIdRef.current) return  // a newer conversation was selected — discard
      const newMsgs = r.data || []
      if (newMsgs.length > 0) {
        if (full) {
          setMessages(newMsgs)
        } else {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.id))
            return [...prev, ...newMsgs.filter(m => !ids.has(m.id))]
          })
        }
        lastIdRef.current = Math.max(...newMsgs.map(m => m.id), lastIdRef.current)
      }
    } catch (e) {
      // non-fatal — chat panel just won't update
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false)
    }
  }, [id, endpoint])

  // Initial full load
  useEffect(() => {
    if (!id) return
    requestIdRef.current += 1   // invalidate any in-flight fetch for the previous conversation
    setMessages([])
    setLoading(true)
    lastIdRef.current = 0
    fetchMessages(true)
  }, [id, scope])

  // Polling — refresh immediately when opened, then 5s when open, 30s when closed
  useEffect(() => {
    if (!id) return
    clearInterval(intervalRef.current)
    // Immediately fetch when panel opens (so new messages appear instantly)
    if (open) fetchMessages(false)
    intervalRef.current = setInterval(() => fetchMessages(false), open ? 5000 : 30000)
    return () => clearInterval(intervalRef.current)
  }, [id, open, fetchMessages])

  const sendMessage = useCallback(async (content, file = null) => {
    if (!content.trim() && !file) return
    setSending(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('content', content.trim() || (file ? file.name : ''))
      if (file) fd.append('file', file)
      const r = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setMessages(prev => {
        const ids = new Set(prev.map(m => m.id))
        return ids.has(r.data.id) ? prev : [...prev, r.data]
      })
      lastIdRef.current = Math.max(lastIdRef.current, r.data.id)
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }, [endpoint])

  const markRead = useCallback(async (projectId) => {
    try { await api.put(`/chat/read/${projectId}`) } catch {}
  }, [])

  return { messages, loading, sending, error, sendMessage, markRead, refresh: () => fetchMessages(true) }
}
